/**
 * Mirror Configurator Modal
 */

(function() {
  'use strict';

  const state = {
    currentStep: 1,
    projectType: 'residential',
    serviceType: 'delivery-installation',
    walls: [
      {
        id: 1,
        size: '36x60',
        finish: 'satin-silver',
        orientation: 'vertical',
        quantity: 1
      }
    ],
    addons: {
      installationFinish: null, // 'standard' | 'premium' | null until user selects
      adhesive: false,
      cutouts: false,
      cutoutsCount: 1,
      outletCoverType: 'light-switch',
      wallSurface: null // set when user selects wall surface
    },
    // Photos uploaded for install reference. Each entry is an object so
    // we can track the background upload state alongside the File:
    //   { file: File, url: string|null, uploadPromise: Promise, failed: bool }
    // - `uploadPromise` resolves when the upload to the dispatch backend
    //   finishes (or fails). handleAddToCart awaits all of these so we
    //   never submit the cart with half-finished uploads.
    // - `url` is the final Supabase Storage CDN URL (set when the upload
    //   resolves successfully). This is what gets attached to the cart
    //   line item as `_photo_${i+1}` — a clean, clickable URL instead of
    //   a multi-KB base64 data string.
    // - `failed` is true if the upload errored. Those photos are silently
    //   dropped from the cart properties (they'd otherwise have no URL
    //   to attach). The console gets a warning so we can see it in the
    //   field.
    installerPhotos: [],
    totalPrice: 0
  };

  // Backend endpoint for install-photo uploads. Configured here (not in
  // a separate env file) because the storefront has no build step — this
  // is just static JS served by Shopify. The URL is public anyway since
  // the route itself is public (validated by MIME + size + CORS).
  const PHOTO_UPLOAD_ENDPOINT = 'https://mirrors-dispatch.vercel.app/api/install-photos/upload';

  // CRO instrumentation — thin safe wrapper around window.mdCroTrack
  // (assets/md-cro-events.js, loaded from theme.liquid). Analytics must
  // never break the configurator, so every call is guarded.
  function track(event, extra) {
    try {
      if (typeof window.mdCroTrack === 'function') window.mdCroTrack(event, extra || {});
    } catch (e) { /* no-op */ }
  }

  // Last step we logged an md_builder_step event for — updateStep() runs
  // on every re-render (price recalcs etc.), so dedupe per step value.
  let lastTrackedStep = null;

  function initModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    const overlay = modal.querySelector('[data-modal-overlay]');
    const closeBtn = modal.querySelector('[data-modal-close]');
    const triggers = document.querySelectorAll('[data-cta-button], [data-open-modal]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });

    // On non-product templates (LP pages embedding this modal), anchors that
    // link to the mirror product open the modal in place instead of
    // navigating. The href stays intact as the no-JS fallback. Gated off the
    // product template so PDP behavior is untouched.
    const tplName = (window._template && window._template.name) || '';
    const mirrorHandle = window.mirrorConfigData && window.mirrorConfigData.mirror && window.mirrorConfigData.mirror.handle;
    if (tplName !== 'product' && mirrorHandle) {
      document.querySelectorAll('a[href*="/products/' + mirrorHandle + '"]').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          openModal();
        });
      });
    }

    if (overlay) {
      overlay.addEventListener('click', closeModal);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) {
        closeModal();
      }
    });
  }

  function openModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Steps 2/3 are display:none until reached, so their lazy images
    // don't fetch until the step shows — users saw blank white option
    // cards at each step transition. Kick off all loads on first open.
    if (!modal.dataset.mdImagesEager) {
      modal.dataset.mdImagesEager = '1';
      modal.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    // Reset summary sections visibility for progressive disclosure
    const summarySections = document.querySelectorAll('[data-summary-section]');
    summarySections.forEach(section => {
      section.classList.remove('mirror-modal__summary-section--visible');
    });

    // Ensure state is reset to step 1 and update visibility
    state.currentStep = 1;
    updateStep();
  }

  function closeModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    modal.classList.remove('is-active');
    document.body.style.overflow = '';

    // Re-opening the builder is a fresh funnel entry — let step events log again
    lastTrackedStep = null;
  }

  function initAccordion() {
    const accordionToggle = document.querySelector('[data-accordion-toggle]');
    const sidebar = document.querySelector('.mirror-modal__sidebar');
    if (!accordionToggle || !sidebar) return;

    accordionToggle.addEventListener('click', () => {
      const isExpanded = accordionToggle.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;
      accordionToggle.setAttribute('aria-expanded', newState);
      
      // Toggle class on sidebar for CSS targeting
      if (newState) {
        sidebar.classList.add('is-expanded');
      } else {
        sidebar.classList.remove('is-expanded');
      }
    });

    // On desktop (>= 1000px), force accordion to be always expanded.
    // Only collapse when CROSSING back below 1000px — mobile browsers
    // fire resize on URL-bar show/hide, and collapsing on every sub-1000
    // resize would fight a user who expanded the summary on purpose.
    let wasDesktop = window.innerWidth >= 1000;
    const checkViewport = () => {
      const isDesktop = window.innerWidth >= 1000;
      if (isDesktop) {
        accordionToggle.setAttribute('aria-expanded', 'true');
        sidebar.classList.add('is-expanded');
      } else if (wasDesktop) {
        accordionToggle.setAttribute('aria-expanded', 'false');
        sidebar.classList.remove('is-expanded');
      }
      wasDesktop = isDesktop;
    };

    if (wasDesktop) checkViewport();
    window.addEventListener('resize', checkViewport);
  }

  function initOptions() {
    const options = document.querySelectorAll('.mirror-modal__option');

    options.forEach(option => {
      const radio = option.querySelector('.mirror-modal__radio');
      if (!radio) return;

      option.addEventListener('click', (e) => {
        e.preventDefault();
        
        const fieldType = radio.getAttribute('data-field');
        const fieldName = radio.getAttribute('name');
        const value = radio.value;

        const allOptionsInGroup = document.querySelectorAll(`input[name="${fieldName}"]`);
        allOptionsInGroup.forEach(r => {
          r.closest('.mirror-modal__option').classList.remove('mirror-modal__option--selected');
        });

        radio.checked = true;
        option.classList.add('mirror-modal__option--selected');

        if (fieldType === 'project-type') {
          state.projectType = value;
        } else if (fieldType === 'service-type') {
          state.serviceType = value;
          if (value !== 'delivery-installation') {
            // Delivery-only: walls don't matter (the customer installs) —
            // collapse to ONE config, and drop install-crew-only selections
            // (cutouts) so hidden choices can never reach the cart.
            resetToSingleWall();
            clearCutoutsSelection();
          }
          updateConditionalFields();
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initButtonOptions() {
    const buttonOptions = document.querySelectorAll('.mirror-modal__button-option, .mirror-modal__orientation-btn');

    buttonOptions.forEach(button => {
      // addNewWall() re-runs this init over ALL walls' controls — guard so
      // existing elements don't accumulate duplicate listeners (which made
      // qty/option taps fire 2-3x after adding walls).
      if (button.dataset.mdBound) return;
      button.dataset.mdBound = '1';
      button.addEventListener('click', (e) => {
        e.preventDefault();

        const field = button.getAttribute('data-field');
        const value = button.getAttribute('data-value');
        
        // Get wall index from parent section
        const wallSection = button.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove active from siblings
        const siblings = button.parentElement.querySelectorAll('.mirror-modal__button-option, .mirror-modal__orientation-btn');
        siblings.forEach(btn => btn.classList.remove('mirror-modal__button-option--active', 'mirror-modal__orientation-btn--active'));

        // Add active to clicked
        button.classList.add(button.classList.contains('mirror-modal__orientation-btn') ? 'mirror-modal__orientation-btn--active' : 'mirror-modal__button-option--active');

        // Update state for this wall
        if (state.walls[wallIndex]) {
          if (field === 'size') {
            state.walls[wallIndex].size = value;
          }
          if (field === 'finish') {
            state.walls[wallIndex].finish = value;
          }
          if (field === 'orientation') {
            state.walls[wallIndex].orientation = value;
          }
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initCardOptions() {
    // Handle size card selections
    const sizeCards = document.querySelectorAll('.mirror-modal__size-card');
    sizeCards.forEach(card => {
      if (card.dataset.mdBound) return; // see initButtonOptions note
      card.dataset.mdBound = '1';
      card.addEventListener('click', (e) => {
        e.preventDefault();

        const radio = card.querySelector('.mirror-modal__size-radio');
        if (!radio) return;

        const field = radio.getAttribute('data-field');
        const value = radio.value;
        
        // Get wall index from parent section
        const wallSection = card.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove selected from siblings
        const siblings = card.parentElement.querySelectorAll('.mirror-modal__size-card');
        siblings.forEach(c => c.classList.remove('mirror-modal__size-card--selected'));

        // Add selected to clicked
        card.classList.add('mirror-modal__size-card--selected');
        radio.checked = true;

        // Update state
        if (state.walls[wallIndex] && field === 'size') {
          state.walls[wallIndex].size = value;
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Handle finish card selections
    const finishCards = document.querySelectorAll('.mirror-modal__finish-card');
    finishCards.forEach(card => {
      if (card.dataset.mdBound) return; // see initButtonOptions note
      card.dataset.mdBound = '1';
      card.addEventListener('click', (e) => {
        e.preventDefault();

        const radio = card.querySelector('.mirror-modal__finish-radio');
        if (!radio) return;

        const field = radio.getAttribute('data-field');
        const value = radio.value;
        
        // Get wall index from parent section
        const wallSection = card.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove selected from siblings
        const siblings = card.parentElement.querySelectorAll('.mirror-modal__finish-card');
        siblings.forEach(c => c.classList.remove('mirror-modal__finish-card--selected'));

        // Add selected to clicked
        card.classList.add('mirror-modal__finish-card--selected');
        radio.checked = true;

        // Update state
        if (state.walls[wallIndex] && field === 'finish') {
          state.walls[wallIndex].finish = value;
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initQuantitySelectors() {
    const qtyButtons = document.querySelectorAll('.mirror-modal__qty-btn');

    qtyButtons.forEach(btn => {
      if (btn.dataset.mdBound) return; // see initButtonOptions note
      btn.dataset.mdBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        const action = btn.getAttribute('data-qty-action');
        const target = btn.getAttribute('data-qty-target') || 'quantity';
        const input = btn.parentElement.querySelector('.mirror-modal__qty-input');
        
        if (!input) return;

        let value = parseInt(input.value) || 0;
        const min = parseInt(input.getAttribute('min')) || 0;
        const max = parseInt(input.getAttribute('max')) || 999;

        if (action === 'increase' && value < max) {
          value++;
        } else if (action === 'decrease' && value > min) {
          value--;
        }

        input.value = value;

        // Update state
        if (target === 'quantity') {
          const wallSection = btn.closest('[data-wall-section]');
          const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;
          if (state.walls[wallIndex]) {
            state.walls[wallIndex].quantity = value;
          }
        } else if (target === 'cutouts') {
          state.addons.cutoutsCount = value;
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Also handle direct input
    const qtyInputs = document.querySelectorAll('.mirror-modal__qty-input');
    qtyInputs.forEach(input => {
      input.addEventListener('change', () => {
        const field = input.getAttribute('data-field');
        const value = parseInt(input.value) || 0;

        if (field === 'quantity') {
          const wallSection = input.closest('[data-wall-section]');
          const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;
          if (state.walls[wallIndex]) {
            state.walls[wallIndex].quantity = value;
          }
        } else if (field === 'cutouts') {
          state.addons.cutoutsCount = value;
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initCheckboxes() {
    // Optional add-ons: independent checkboxes (none / adhesive / cutouts / both)
    const addonInputs = document.querySelectorAll('[data-addon]');

    addonInputs.forEach(input => {
      input.addEventListener('change', () => {
        const addon = input.getAttribute('data-addon');
        if (addon === 'adhesive') {
          state.addons.adhesive = input.checked;
        } else if (addon === 'cutouts') {
          state.addons.cutouts = input.checked;
          toggleCutoutDetails(input.checked);
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Handle installation finish radio buttons
    const installFinishRadios = document.querySelectorAll('[data-field="installation-finish"]');
    
    installFinishRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        e.preventDefault();
        state.addons.installationFinish = radio.value;
        updateSummary();
        calculatePrice();
      });

      // Prevent scroll on label click
      const label = radio.closest('.mirror-modal__addon-card');
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });

    // Handle wall surface radio buttons
    const wallSurfaceRadios = document.querySelectorAll('[data-field="wall-surface"]');
    
    wallSurfaceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        e.preventDefault();
        state.addons.wallSurface = radio.value;
        updateSummary();
        calculatePrice();
      });

      // Prevent scroll on label click
      const label = radio.closest('.mirror-modal__radio-option');
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  function toggleCutoutDetails(show) {
    const cutoutDetails = document.querySelector('[data-conditional="cutouts-only"]');
    if (cutoutDetails) {
      cutoutDetails.style.display = show ? 'block' : 'none';
    }
  }

  // Delivery-only: collapse the builder to a single configuration. Walls
  // only matter when our crew installs (per-wall labels + laydowns); for
  // DIY the customer just needs size/quantity/orientation/finish. Extra
  // wall sections are REMOVED (not hidden) — a hidden wall would still
  // submit its mirrors and bill for them.
  function resetToSingleWall() {
    document.querySelectorAll('[data-wall-section]').forEach((section) => {
      const idx = parseInt(section.getAttribute('data-wall-section'), 10);
      if (idx > 0) section.remove();
    });
    if (state.walls.length > 1) {
      state.walls.length = 1;
    }
    updateRemoveButtonsVisibility();
  }

  // Cutouts are an install-crew service (we cut on site). Delivery-only
  // ships full uncut mirrors, so deselect + reset when switching.
  function clearCutoutsSelection() {
    state.addons.cutouts = false;
    document.querySelectorAll('[data-addon="cutouts"]').forEach((input) => {
      input.checked = false;
    });
    toggleCutoutDetails(false);
  }

  function initSelects() {
    // Cutouts count select
    const cutoutsCountSelect = document.querySelector('[data-field="cutouts-count"]');
    
    if (cutoutsCountSelect) {
      cutoutsCountSelect.addEventListener('change', (e) => {
        state.addons.cutoutsCount = parseInt(e.target.value, 10) || 1;
        updateSummary();
        calculatePrice();
      });
    }

    // Outlet cover type select
    const outletCoverSelect = document.querySelector('[data-field="outlet-cover-type"]');
    
    if (outletCoverSelect) {
      outletCoverSelect.addEventListener('change', (e) => {
        state.addons.outletCoverType = e.target.value;
        // "Not sure — I'll upload a photo": reveal the pointer to the photo
        // uploader below so the install team gets a picture to match covers.
        const coverHint = document.querySelector('[data-cover-photo-hint]');
        if (coverHint) coverHint.hidden = e.target.value !== 'not-sure';
        updateSummary();
        calculatePrice();
      });
    }

    // Wall surface select
    const wallSurfaceSelect = document.querySelector('[data-field="wall-surface"]');
    
    if (wallSurfaceSelect) {
      wallSurfaceSelect.addEventListener('change', (e) => {
        state.addons.wallSurface = e.target.value;
        updateSummary();
        calculatePrice();
      });
    }
  }

  function initPhotoUpload() {
    const photoAddBtn = document.querySelector('[data-photo-add-btn]');
    const photoInput = document.querySelector('[data-photo-input]');
    const photoUploadArea = document.querySelector('[data-photo-upload-area]');

    if (photoAddBtn && photoInput) {
      photoAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        photoInput.click();
      });

      photoInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
          if (file.type.startsWith('image/')) {
            addPhotoPreview(file, photoUploadArea, photoAddBtn);
          }
        });
        photoInput.value = ''; // Reset input
      });
    }
  }

  // Compress client-side before upload. Modern phone photos run 4–15MB
  // and the upload endpoint's platform ceiling is ~4.5MB (Vercel rejects
  // bigger bodies before our code runs) — plus a ~400KB JPEG uploads
  // 10-20x faster on cellular. Downscales to ≤2048px on the long edge
  // and re-encodes as JPEG. Canvas decodes anything the browser can
  // open, so iPhone HEIC becomes JPEG right here on Safari. ANY failure
  // falls back to the original file — the server has its own normalize
  // pass, so compression is an optimization, never a gate.
  const MAX_UPLOAD_EDGE = 2048;
  const UPLOAD_JPEG_QUALITY = 0.82;

  async function prepareForUpload(file) {
    try {
      if (!file || !file.type || file.type.indexOf('image/') !== 0) return file;
      // Already a small JPEG — recompression can only add artifacts.
      if (file.size < 700 * 1024 && file.type === 'image/jpeg') return file;

      let bitmap = null;
      if (typeof createImageBitmap === 'function') {
        try {
          // from-image = bake in EXIF orientation so phone landscape
          // shots don't end up sideways after re-encode.
          bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
          try { bitmap = await createImageBitmap(file); } catch (e2) { bitmap = null; }
        }
      }

      let width, height, source;
      if (bitmap) {
        width = bitmap.width; height = bitmap.height; source = bitmap;
      } else {
        source = await new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
          img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
          img.src = url;
        });
        width = source.naturalWidth; height = source.naturalHeight;
      }
      if (!width || !height) return file;

      const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(width, height));
      const outW = Math.max(1, Math.round(width * scale));
      const outH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(source, 0, 0, outW, outH);
      if (bitmap && bitmap.close) bitmap.close();

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', UPLOAD_JPEG_QUALITY));
      if (!blob || blob.size === 0) return file;
      // Keep whichever is smaller (an already-optimized JPEG shouldn't grow).
      if (blob.size >= file.size && file.type === 'image/jpeg') return file;
      const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
      return new File([blob], base + '.jpg', { type: 'image/jpeg' });
    } catch (e) {
      return file; // compression must never block an upload
    }
  }

  // Upload a single image to the dispatch backend, which stores it in
  // Supabase Storage and returns a public CDN URL. The URL is small
  // enough to fit comfortably in a Shopify cart line-item property
  // (a Supabase public URL is ~120 chars), and renders as a real
  // clickable thumbnail in the Shopify Admin order page instead of
  // the giant base64 blob we used to send.
  //
  // Returns { ok: true, url } on success, { ok: false, error } on failure.
  // Never throws — handleAddToCart awaits this and just skips photos
  // whose upload failed.
  async function uploadPhoto(file) {
    try {
      const prepared = await prepareForUpload(file);
      const formData = new FormData();
      formData.append('file', prepared, prepared.name || 'photo.jpg');
      const res = await fetch(PHOTO_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data || !data.ok || !data.url) {
        const errMsg = (data && (data.error || data.detail)) || ('http_' + res.status);
        return { ok: false, error: errMsg };
      }
      return { ok: true, url: data.url };
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'network_error' };
    }
  }

  function addPhotoPreview(file, container, addBtn) {
    // Build the state entry first so the upload promise is in flight
    // before the FileReader resolves. iOS phone photos take ~2-4s to
    // upload on cellular; the user usually adds 1-3 photos then keeps
    // configuring, so the uploads can finish in the background while
    // they navigate steps 2-3. By the time they tap "Add to Cart" the
    // promises are usually already resolved.
    const photoEntry = {
      file,
      url: null,
      failed: false,
      uploadPromise: null,
      _statusEl: null,
    };

    // Visible per-photo upload state. Now that a photo is REQUIRED for
    // install orders, a silently-failed upload means the crew gets no
    // picture — so failures show on the thumbnail with tap-to-retry
    // instead of only console.warn.
    function setStatus(kind) {
      const el = photoEntry._statusEl;
      if (!el) return;
      el.classList.remove('is-uploading', 'is-failed');
      if (kind === 'uploading') {
        el.classList.add('is-uploading');
        el.textContent = 'Uploading…';
      } else if (kind === 'failed') {
        el.classList.add('is-failed');
        el.textContent = '⚠ Failed — tap to retry';
      } else {
        el.textContent = '';
      }
    }

    function startUpload() {
      photoEntry.failed = false;
      setStatus('uploading');
      photoEntry.uploadPromise = uploadPhoto(photoEntry.file).then((result) => {
        if (result.ok) {
          photoEntry.url = result.url;
          photoEntry.failed = false;
          setStatus('done');
        } else {
          photoEntry.failed = true;
          setStatus('failed');
          if (window.console) {
            console.warn('[mirror-builder] photo upload failed:', result.error, '(file:', photoEntry.file.name, ')');
          }
        }
      });
    }
    startUpload();

    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = document.createElement('div');
      preview.className = 'mirror-modal__photo-preview';

      const img = document.createElement('img');
      img.className = 'mirror-modal__photo-preview-image';
      img.src = e.target.result;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'mirror-modal__photo-remove-btn';
      removeBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        preview.remove();
        const index = state.installerPhotos.indexOf(photoEntry);
        if (index > -1) {
          state.installerPhotos.splice(index, 1);
        }
      });

      const statusBtn = document.createElement('button');
      statusBtn.type = 'button';
      statusBtn.className = 'mirror-modal__photo-status';
      statusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (photoEntry.failed) startUpload();
      });

      preview.appendChild(img);
      preview.appendChild(removeBtn);
      preview.appendChild(statusBtn);
      photoEntry._statusEl = statusBtn;
      // The upload races the FileReader — reflect wherever it already is.
      setStatus(photoEntry.failed ? 'failed' : (photoEntry.url ? 'done' : 'uploading'));

      // Insert before the add button
      container.insertBefore(preview, addBtn);
    };
    reader.readAsDataURL(file);

    // Push to state immediately (not inside reader.onload) so the entry
    // exists from the moment the user selects the file — the upload is
    // already in flight at this point, and we don't want it orphaned if
    // FileReader is slow (it usually isn't, but defensive).
    state.installerPhotos.push(photoEntry);
  }

  function initNavigation() {
    const prevBtn = document.querySelector('[data-nav="prev"]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');

    // Remember the full "Add to Cart & Schedule Installation" label so
    // updateStep() can swap it back in on step 3 (it shows "Continue"
    // during steps 1–2 — see advanceOrSubmit below).
    [submitBtn, submitBtnCollapsed].forEach((btn) => {
      if (btn && !btn.dataset.fullLabel) btn.dataset.fullLabel = btn.textContent.trim();
    });

    // Debounce step navigation. On slow mobile devices the step swap can
    // lag a beat behind the tap; impatient users double-tap and used to
    // jump TWO steps (1 → 3), skipping size/finish selection entirely.
    let lastNavAt = 0;
    function navDebounced() {
      const now = Date.now();
      if (now - lastNavAt < 350) return false;
      lastNavAt = now;
      return true;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (!navDebounced()) return;
        if (state.currentStep > 1) {
          state.currentStep--;
          updateStep();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (!navDebounced()) return;
        if (state.currentStep < 3) {
          state.currentStep++;
          updateStep();
        } else {
          // Step 3: Next IS Add to Cart — on every viewport. This used to
          // be desktop-only (window.innerWidth >= 1000), which made the
          // button a literal no-op on mobile.
          handleAddToCart();
        }
      });
    }

    // The sticky submit buttons used to be disabled until step 3 — on
    // mobile that's the biggest button in the thumb zone, and every tap
    // on it was a dead click (Clarity logged ~1,100/wk on the text right
    // next to it). Now they're always live: before step 3 they act as
    // "Continue" (same as Next), on step 3 they submit.
    function advanceOrSubmit() {
      if (state.currentStep < 3) {
        if (!navDebounced()) return;
        track('md_cta_click', { detail: { id: 'builder-submit-early', step: String(state.currentStep) } });
        state.currentStep++;
        updateStep();
      } else {
        handleAddToCart();
      }
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', advanceOrSubmit);
    }

    if (submitBtnCollapsed) {
      submitBtnCollapsed.addEventListener('click', advanceOrSubmit);
    }
  }

  // Inline validation highlight — replaces alert() dialogs, which are
  // jarring on mobile (full-screen takeover, then the user has to hunt
  // for the field themselves). Scrolls the offending field into view
  // and pulses a red outline instead. Falls back to alert() if the
  // field can't be found (e.g. markup changed underneath us).
  function flagMissingField(inputName, fallbackMessage) {
    const input = document.querySelector('input[name="' + inputName + '"]');
    const field = input ? input.closest('.mirror-modal__field') : null;
    if (!field) {
      alert(fallbackMessage);
      return;
    }
    field.classList.add('mirror-modal__field--error');
    let msg = field.querySelector('.mirror-modal__field-error-msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'mirror-modal__field-error-msg';
      msg.setAttribute('role', 'alert');
      const heading = field.querySelector('.mirror-modal__label');
      if (heading) heading.insertAdjacentElement('afterend', msg);
      else field.prepend(msg);
    }
    msg.textContent = fallbackMessage;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Error stays until the user interacts with the field — a timed
    // dismissal left users staring at an unexplained non-working button
    // (Clarity showed repeat-click confusion on step 3 submits).
    if (!field.dataset.mdErrorBound) {
      field.dataset.mdErrorBound = '1';
      field.addEventListener('click', () => {
        field.classList.remove('mirror-modal__field--error');
        const m = field.querySelector('.mirror-modal__field-error-msg');
        if (m) m.remove();
      });
    }
  }

  async function handleAddToCart() {
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    // Next doubles as Add to Cart on step 3 — on EVERY viewport (this
    // used to be desktop-only, leaving mobile's Next a dead button).
    const isLastStep = state.currentStep === 3;

    if (!submitBtn && !submitBtnCollapsed) return;

    const originalText = submitBtn ? submitBtn.textContent : '';
    const originalTextCollapsed = submitBtnCollapsed ? submitBtnCollapsed.textContent : '';

    function restoreSubmitState() {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
      if (submitBtnCollapsed) {
        submitBtnCollapsed.disabled = false;
        submitBtnCollapsed.textContent = originalTextCollapsed;
      }
      if (isLastStep && nextBtn) {
        nextBtn.disabled = false;
        nextBtn.classList.remove('mirror-modal__btn--loading');
      }
    }

    // Disable all buttons and show loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding to Cart...';
    }
    if (submitBtnCollapsed) {
      submitBtnCollapsed.disabled = true;
      submitBtnCollapsed.textContent = 'Adding to Cart...';
    }
    if (isLastStep && nextBtn) {
      nextBtn.disabled = true;
      nextBtn.classList.add('mirror-modal__btn--loading');
    }

    try {
      const productData = window.mirrorConfigData || {};
      const mirrorProduct = productData.mirror;
      const addons = productData.addons || {};

      if (!mirrorProduct) {
        throw new Error('Mirror product data not available');
      }

      // Installation finish is required for BOTH services — for install
      // jobs it's the crew spec; for delivery-only it tells us which top
      // hardware to ship (Standard = top clips, Premium = top J-channel).
      if (!state.addons.installationFinish) {
        flagMissingField('installation-finish', 'Please select an installation finish (Standard or Premium).');
        track('md_error', { detail: { kind: 'builder_validation', field: 'installation-finish' } });
        restoreSubmitState();
        return;
      }
      if (state.serviceType === 'delivery-installation') {
        if (!state.addons.wallSurface) {
          flagMissingField('wall-surface', 'Please select a wall surface.');
          track('md_error', { detail: { kind: 'builder_validation', field: 'wall-surface' } });
          restoreSubmitState();
          return;
        }
        // Wall photo is REQUIRED for install jobs (2026-08, was optional):
        // crews kept arriving to surprises — outlet positions, baseboards,
        // wainscoting — that one photo would have caught. Only "did they
        // ADD a photo" is enforced; a failed upload still passes (photos
        // stay best-effort so flaky cellular can't block a sale).
        // Delivery-only customers never see the uploader and are exempt.
        if (state.installerPhotos.length === 0) {
          flagMissingField('install-photos', 'Please add at least one photo of your wall — our install team uses it to bring the right hardware and covers.');
          track('md_error', { detail: { kind: 'builder_validation', field: 'install-photos' } });
          restoreSubmitState();
          return;
        }
      }

      // Build items array for JSON submission
      const items = [];

      // Wait for any still-in-flight photo uploads to finish. Uploads
      // are kicked off immediately when the user picks a file (see
      // addPhotoPreview + uploadPhoto), so most of the time these
      // promises are already resolved by the time the user hits
      // "Add to Cart" — they've spent 30+ seconds on the configurator
      // between picking the photo and submitting.
      //
      // If a photo's upload failed, we skip it silently (drop the photo
      // from cart properties). Better to lose the picture than to send
      // a multi-KB base64 blob — that was the whole reason we built
      // this URL-based path in the first place.
      await Promise.all(state.installerPhotos.map((p) => p.uploadPromise));

      const photoUrls = state.installerPhotos
        .filter((p) => p.url && !p.failed)
        .map((p) => p.url);

      const droppedCount = state.installerPhotos.length - photoUrls.length;
      if (droppedCount > 0 && window.console) {
        console.warn(
          '[mirror-builder]',
          droppedCount,
          'of',
          state.installerPhotos.length,
          'uploaded photos failed to upload and were dropped from the cart attribute.'
        );
      }

      // Shared display properties applied to each wall line item.
      // Includes only attributes that genuinely describe each wall's
      // mirrors (Service, Installation finish, Wall surface).
      //
      // Cutouts and Adhesive are DELIBERATELY NOT included here even
      // though they're project-level selections — they show up as
      // their own separate line items in the cart (added below), and
      // spreading them onto every wall caused customer confusion:
      // ordering "1 cutout" displayed as "Cutouts: 1" on Wall 1 AND
      // Wall 2, reading as "1 per wall = 2 total" when really it's
      // 1 cutout for the whole project.
      const serviceLabel = state.serviceType === 'delivery-installation'
        ? 'Delivery + Installation' : 'Delivery Only (DIY)';

      const sharedAddonProps = {};
      sharedAddonProps['Service'] = serviceLabel;

      if (state.addons.installationFinish) {
        // Written for BOTH services — dispatch reads this to know which top
        // hardware ships with the order (clips vs J-channel).
        sharedAddonProps['Installation finish'] = state.addons.installationFinish === 'premium' ? 'Premium' : 'Standard';
      }
      if (state.serviceType === 'delivery-installation' && state.addons.wallSurface) {
        sharedAddonProps['Wall surface'] = getWallSurfaceLabel(state.addons.wallSurface);
      }

      // Add mirror products for each wall
      for (const [index, wall] of state.walls.entries()) {
        const variant = getMirrorVariant(mirrorProduct, wall.size, wall.finish);
        
        if (variant) {
          const props = {
            _config_id: `WALL-${index + 1}`,
            _wall_label: `Wall ${index + 1}`,
            _project_type: state.projectType,
            _service_type: state.serviceType,
            _mirror_size: getSizeLabel(wall.size),
            _channel_finish: getFinishLabel(wall.finish),
            _orientation: getOrientationLabel(wall.orientation),
            _quantity_for_wall: wall.quantity,
            'Size': getSizeLabel(wall.size),
            'Mirror Orientation': getOrientationLabel(wall.orientation),
            'Channel finish': getFinishLabel(wall.finish),
            ...sharedAddonProps
          };

          if (index === 0 && photoUrls.length > 0) {
            photoUrls.forEach((url, i) => {
              props[`_photo_${i + 1}`] = url;
            });
          }

          const mirrorItem = {
            id: variant.id,
            quantity: wall.quantity,
            properties: props
          };
          items.push(mirrorItem);
        } else {
          throw new Error(`Could not find variant for ${getSizeLabel(wall.size)} / ${getFinishLabel(wall.finish)}`);
        }
      }

      // Calculate total mirrors
      const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

      // Add installation (if selected)
      if (state.serviceType === 'delivery-installation' && addons.installation) {
        const installationVariant = addons.installation.variants?.[0];
        if (installationVariant && installationVariant.id) {
          const installationItem = {
            id: installationVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'installation',
              _applies_to_mirrors: totalMirrors,
              _project_type: state.projectType
            }
          };
          items.push(installationItem);
        }
      }

      // Add premium finish (if selected)
      if (state.addons.installationFinish === 'premium' && addons.premiumFinish) {
        const premiumVariant = addons.premiumFinish.variants?.[0];
        if (premiumVariant && premiumVariant.id) {
          const premiumItem = {
            id: premiumVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'premium_finish',
              _applies_to_mirrors: totalMirrors
            }
          };
          items.push(premiumItem);
        }
      }

      // Add adhesive (if selected)
      if (state.addons.adhesive && addons.adhesive) {
        const adhesiveVariant = addons.adhesive.variants?.[0];
        if (adhesiveVariant && adhesiveVariant.id) {
          // One tube per mirror (priced per mirror since 2026-10-07; was one flat tube).
          const adhesiveItem = {
            id: adhesiveVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'adhesive',
              _applies_to_mirrors: totalMirrors
            }
          };
          items.push(adhesiveItem);
        }
      }

      // Add cutouts (if selected)
      if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
        const cutoutVariant = addons.cutout.variants?.[0];
        if (cutoutVariant && cutoutVariant.id) {
          const cutoutItem = {
            id: cutoutVariant.id,
            quantity: state.addons.cutoutsCount,
            properties: {
              _addon_type: 'cutout',
              _cutouts_count: state.addons.cutoutsCount,
              _outlet_cover_type: state.addons.outletCoverType
            }
          };
          items.push(cutoutItem);
        }
      }

      // Add wall surface surcharge (if needed)
      if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
        const surchargeVariant = addons.wallSurcharge.variants?.[0];
        if (surchargeVariant && surchargeVariant.id) {
          const surchargeItem = {
            id: surchargeVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'wall_surcharge',
              _wall_surface: state.addons.wallSurface,
              _applies_to_mirrors: totalMirrors
            }
          };
          items.push(surchargeItem);
        }
      }

      // ─────────────────────────────────────────────────────────
      // PRE-SUBMISSION GUARD — never submit an under-billed cart.
      // ─────────────────────────────────────────────────────────
      // Background: order #3290 (2026-05-28) shipped with the mirror
      // line item only — no installation, no premium finish — even
      // though the customer's mirror properties said "Service: Delivery
      // + Installation / Installation finish: Premium". They were
      // undercharged ~$300. The most likely cause was a
      // window.mirrorConfigData.addons object that didn't have all the
      // expected products loaded (the descriptive properties survive
      // because they come from state, but the actual line items get
      // silently skipped by the `if (... && addons.installation)`
      // guards above).
      //
      // This block re-checks AFTER all the conditional pushes above:
      // for every addon the user selected, did a corresponding line
      // item actually make it into `items`? If anything's missing,
      // abort with a visible error so dispatch never sees another
      // mystery under-billed order.
      //
      // Detection works by looking for the expected _addon_type values
      // in the items we're about to submit, rather than re-checking the
      // addons object — that way we catch ANY reason an item didn't
      // get pushed (missing variant, missing product config, missing
      // ID on the variant, etc.) with one check.
      const submittedAddonTypes = new Set(
        items
          .map((it) => it.properties && it.properties._addon_type)
          .filter(Boolean)
      );
      const missingAddons = [];
      if (state.serviceType === 'delivery-installation') {
        if (!submittedAddonTypes.has('installation')) {
          missingAddons.push('Professional Installation');
        }
        if (needsWallSurcharge(state.addons.wallSurface) && !submittedAddonTypes.has('wall_surcharge')) {
          missingAddons.push('Wall Surface Surcharge');
        }
      }
      // Premium finish charges on BOTH services (delivery-only premium =
      // top J-channel hardware shipped with the order).
      if (state.addons.installationFinish === 'premium' && !submittedAddonTypes.has('premium_finish')) {
        missingAddons.push('Premium Installation Finish');
      }
      if (state.addons.adhesive && !submittedAddonTypes.has('adhesive')) {
        missingAddons.push('Adhesive');
      }
      if (state.addons.cutouts && state.addons.cutoutsCount > 0 && !submittedAddonTypes.has('cutout')) {
        missingAddons.push('Outlet/Switch Cutout');
      }

      if (missingAddons.length > 0) {
        if (window.console) {
          console.error(
            '[mirror-builder] ABORTING cart submission — required add-ons missing from items:',
            missingAddons,
            { state, addons, items }
          );
        }
        alert(
          "We hit a snag adding the following items to your cart:\n\n" +
            missingAddons.map((s) => '  • ' + s).join('\n') +
            "\n\nPlease refresh the page and try again. If this keeps happening, please email support@mirrorsdelivered.com or give us a call — we want to make sure you're charged correctly."
        );
        restoreSubmitState();
        return;
      }

      // Replace any existing builder lines before adding. /cart/add.js
      // APPENDS, so without this a customer who rebuilds and re-adds
      // stacks a second set of add-ons (over-charge, e.g. order #3377)
      // or leaves orphaned add-ons behind. Each add-to-cart represents
      // the COMPLETE current configuration, so we clear the cart's
      // builder contents first, then add the fresh set.
      await removeExistingBuilderLines();

      // Submit via fetch with JSON
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();

      // Success - redirect to cart. The beacon transport in
      // md-cro-events.js survives the navigation.
      track('md_builder_complete', {
        value: state.totalPrice,
        detail: { walls: String(state.walls.length) }
      });
      window.location.href = '/cart';

    } catch (error) {
      track('md_error', {
        detail: { kind: 'builder_cart_error', message: String(error && error.message).slice(0, 120) }
      });
      alert('Could not add to cart. Please try again.\n\nError: ' + error.message);
      restoreSubmitState();
    }
  }

  // Remove any Mirror Builder lines already in the cart before adding the
  // current configuration. Builder mirror lines carry `_config_id`;
  // add-on lines carry `_addon_type`. Without this, /cart/add.js APPENDS,
  // so a rebuild-and-re-add stacks duplicate add-ons (over-charge,
  // order #3377) or leaves stale add-ons orphaned alongside new mirrors.
  // Non-builder products (e.g. the legacy Gym Wall Mirror Kit) have
  // neither property and are left untouched. Best-effort: any failure is
  // non-fatal — the server-side cart validator (mirrors-scheduler) is the
  // backstop that guarantees add-on and mirror quantities match.
  async function removeExistingBuilderLines() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const updates = {};
      (cart.items || []).forEach((item) => {
        const props = item.properties || {};
        if (props._config_id || props._addon_type) {
          updates[item.key] = 0;
        }
      });
      if (Object.keys(updates).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
    } catch (e) {
      if (window.console) {
        console.warn('[mirror-builder] could not clear prior builder lines:', e);
      }
    }
  }

  function normalizeOptionString(str) {
    let s = str || '';
    try {
      if (typeof s.normalize === 'function') {
        s = s.normalize('NFKD');
      }
    } catch (e) {
      /* ignore */
    }
    return s
      .replace(/[\u2018\u2019\u2032\u2035\uFF07]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .toLowerCase();
  }

  function getMirrorVariantsSource(mirrorProduct) {
    const data = window.mirrorConfigData || {};
    if (Array.isArray(data.mirrorVariantCatalog) && data.mirrorVariantCatalog.length > 0) {
      return data.mirrorVariantCatalog;
    }
    if (mirrorProduct && Array.isArray(mirrorProduct.variants) && mirrorProduct.variants.length > 0) {
      return mirrorProduct.variants;
    }
    return [];
  }

  function getVariantSearchString(v) {
    return normalizeOptionString([
      v.title || '',
      v.option1 || '',
      v.option2 || '',
      v.option3 || ''
    ].join(' '));
  }

  function variantMatchesConfiguratorSize(optStr, size) {
    if (size === '36x60') {
      if (/\b36\s*[x×]\s*60\b/.test(optStr)) return true;
      if (/3\s*[''′]?\s*[x×]\s*5\s*[''′]?/.test(optStr)) return true;
      if (/\b3\s*[x×]\s*5\b/.test(optStr)) return true;
      if (optStr.includes('36x60')) return true;
      if (optStr.includes('36') && optStr.includes('60')) return true;
      return optStr.includes('36') && /(^|[^0-9])60([^0-9]|$)/.test(optStr);
    }
    if (size === '48x72') {
      if (/\b48\s*[x×]\s*72\b/.test(optStr)) return true;
      if (/4\s*[''′]?\s*[x×]\s*6\s*[''′]?/.test(optStr)) return true;
      if (/\b4\s*[x×]\s*6\b/.test(optStr)) return true;
      if (optStr.includes('48x72')) return true;
      if (optStr.includes('48') && optStr.includes('72')) return true;
      return optStr.includes('48') && /(^|[^0-9])72([^0-9]|$)/.test(optStr);
    }
    return false;
  }

  function variantMatchesConfiguratorFinish(optStr, finish) {
    if (finish === 'satin-silver') {
      if (optStr.includes('matte')) return false;
      return optStr.includes('satin') || optStr.includes('silver');
    }
    if (finish === 'matte-black') {
      return optStr.includes('matte') || /\bblack\b/.test(optStr);
    }
    return false;
  }

  function findMirrorVariant(mirrorProduct, size, finish) {
    const variants = getMirrorVariantsSource(mirrorProduct);
    if (!variants.length) {
      return null;
    }

    let match = variants.find(v => {
      const s = getVariantSearchString(v);
      return variantMatchesConfiguratorSize(s, size) && variantMatchesConfiguratorFinish(s, finish);
    });
    if (match) {
      return match;
    }

    const normalizedSize = size === '36x60' ? '36' : '48';
    const normalizedFinish = finish === 'satin-silver' ? 'satin' : 'black';

    match = variants.find(v => {
      const allOptions = [
        (v.option1 || '').toLowerCase(),
        (v.option2 || '').toLowerCase(),
        (v.option3 || '').toLowerCase()
      ].join(' ');

      return allOptions.includes(normalizedSize) && allOptions.includes(normalizedFinish);
    });
    if (match) {
      return match;
    }

    const sizeMatches = variants.filter(v => {
      const s = getVariantSearchString(v);
      return variantMatchesConfiguratorSize(s, size);
    });

    if (sizeMatches.length) {
      match = sizeMatches.find(v => {
        const s = getVariantSearchString(v);
        return variantMatchesConfiguratorFinish(s, finish);
      });
      if (match) {
        return match;
      }
      return sizeMatches[0];
    }

    return null;
  }

  function getMirrorVariant(mirrorProduct, size, finish) {
    return findMirrorVariant(mirrorProduct, size, finish);
  }

  function updateStep() {
    const steps = document.querySelectorAll('[data-step]');
    const progressDots = document.querySelectorAll('.mirror-modal__progress-dot');
    const progressLabel = document.querySelector('.mirror-modal__progress-label');
    const prevBtn = document.querySelector('[data-nav="prev"]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');
    const modalLayout = document.querySelector('.mirror-modal__layout');

    // Hide all steps
    steps.forEach(step => {
      step.style.display = 'none';
    });

    // Show current step
    const currentStepEl = document.querySelector(`[data-step="${state.currentStep}"]`);
    if (currentStepEl) {
      currentStepEl.style.display = 'block';
    }

    // Per-step heading + "Step X of 3" count so customers always know
    // where they are (the heading used to read "Configure Your Mirrors"
    // on all three steps, with only the tiny dots changing). Step 3 is
    // service-aware: DIY customers pick HARDWARE (clips vs J-channel),
    // not an installation.
    const isInstallService = state.serviceType === 'delivery-installation';
    const STEP_TITLES = {
      1: 'Project & Service',
      2: 'Design Your Mirrors',
      3: isInstallService ? 'Installation & Add-ons' : 'Hardware & Add-ons'
    };
    const progressCount = document.querySelector('[data-progress-count]');
    if (progressLabel) {
      progressLabel.textContent = STEP_TITLES[state.currentStep] || 'Configure Your Mirrors';
    }
    if (progressCount) {
      progressCount.textContent = 'Step ' + state.currentStep + ' of 3';
    }

    // Update progress dots - only current step is active (blue and elongated)
    progressDots.forEach((dot, index) => {
      if (index + 1 === state.currentStep) {
        dot.classList.add('mirror-modal__progress-dot--active');
      } else {
        dot.classList.remove('mirror-modal__progress-dot--active');
      }
    });

    // Progressive disclosure of order summary sections
    const projectServiceSection = document.querySelector('[data-summary-section="project-service"]');
    const wallsSection = document.querySelector('[data-summary-section="walls"]');
    const pricingSection = document.querySelector('[data-summary-section="pricing"]');

    if (projectServiceSection) {
      // Show project/service info after completing step 1
      if (state.currentStep >= 1) {
        projectServiceSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    if (wallsSection) {
      // Show walls info after moving to step 2 or later
      if (state.currentStep >= 2) {
        wallsSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    if (pricingSection) {
      // Show pricing breakdown on step 3
      if (state.currentStep >= 3) {
        pricingSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    // Update navigation buttons
    if (prevBtn) {
      prevBtn.disabled = state.currentStep === 1;
    }

    // Toggle step-3 class on modal root for CSS targeting
    const modalRoot = document.querySelector('[data-mirror-modal]');
    if (modalRoot) {
      modalRoot.classList.toggle('mirror-modal--step-3', state.currentStep === 3);
    }

    if (nextBtn) {
      // On step 3 (any viewport) it acts as Add to Cart — keep enabled
      nextBtn.disabled = false;
    }

    // Submit buttons stay enabled on every step. Before step 3 they act
    // as "Continue" (initNavigation's advanceOrSubmit); disabling them
    // made the biggest button in the mobile thumb zone a dead-click trap
    // during steps 1-2. Label reflects what the button will do.
    [submitBtn, submitBtnCollapsed].forEach((btn) => {
      if (!btn) return;
      btn.disabled = false;
      if (state.currentStep < 3) {
        // Name the destination — this is the ONE primary button per step
        // (the old bottom "Next" was removed; two blue buttons with
        // different labels competed for the same click).
        btn.textContent = state.currentStep === 1
          ? 'Continue to Mirrors'
          : (isInstallService ? 'Continue to Installation' : 'Continue to Hardware');
      } else {
        // Final label is service-aware: install orders schedule an
        // installation, DIY orders schedule a delivery.
        btn.textContent = isInstallService
          ? (btn.dataset.fullLabel || 'Add to Cart & Schedule Installation')
          : (btn.dataset.fullLabelDelivery || 'Add to Cart & Schedule Delivery');
      }
    });

    // Funnel instrumentation: one event per step actually VIEWED inside
    // the open modal. init() calls updateStep() on page load with the
    // modal closed — the is-active gate keeps that from logging a fake
    // step-1 on every pageview. lastTrackedStep dedupes re-renders of
    // the same step (price recalcs etc. also call updateStep).
    if (modalRoot && modalRoot.classList.contains('is-active') && state.currentStep !== lastTrackedStep) {
      lastTrackedStep = state.currentStep;
      track('md_builder_step', { step: state.currentStep });
    }

    updateConditionalFields();
    updateSummary();
    calculatePrice();

    // Scroll to top AFTER all DOM changes so the browser has settled the layout.
    // On mobile the sticky sidebar resizes when summary sections appear, which
    // cancels a smooth scroll that was started before the reflow.
    if (modalLayout) {
      requestAnimationFrame(() => {
        modalLayout.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  function updateConditionalFields() {
    const isInstall = state.serviceType === 'delivery-installation';
    const installationOnlyFields = document.querySelectorAll('[data-conditional="installation-only"]');

    installationOnlyFields.forEach(field => {
      if (isInstall) {
        field.style.display = 'flex';
      } else {
        field.style.display = 'none';
      }
    });

    // Root class drives the delivery-only CSS (hides the wall header,
    // add-wall button, cutout add-on, and photo upload — see
    // modal-mirror-configurator.css "Delivery-only mode").
    const modalRoot = document.querySelector('[data-mirror-modal]');
    if (modalRoot) {
      modalRoot.classList.toggle('mirror-modal--delivery-only', !isInstall);
    }

    // No crew on delivery-only orders — the estimate line drops the
    // "and installation". Both variants come from data attributes the
    // Liquid renders (theme-editable), with hardcoded fallbacks.
    const estimateEl = document.querySelector('.mirror-modal__estimate');
    if (estimateEl) {
      estimateEl.textContent = isInstall
        ? (estimateEl.dataset.estimateInstall || 'Estimated delivery and installation within 3–10 days')
        : (estimateEl.dataset.estimateDelivery || 'Estimated delivery within 3–10 days');
    }
  }

  function updateSummary() {
    const projectEl = document.querySelector('[data-summary="project"]');
    const serviceEl = document.querySelector('[data-summary="service"]');

    if (projectEl) {
      projectEl.textContent = state.projectType === 'residential' ? 'Residential' : 'Commercial';
    }

    if (serviceEl) {
      const serviceText = state.serviceType === 'delivery-installation' 
        ? 'Delivery + Installation' 
        : 'Delivery Only';
      serviceEl.textContent = serviceText;
    }

    updateSummaryDetails();
  }

  function updateSummaryDetails() {
    // Update walls summary
    const wallsContainer = document.querySelector('[data-summary-walls]');
    if (wallsContainer) {
      wallsContainer.innerHTML = '';
      
      // Calculate totals
      const totalWalls = state.walls.length;
      let qty36x60 = 0;
      let qty48x72 = 0;
      const finishes = {};
      
      state.walls.forEach(wall => {
        // Aggregate quantities by size
        if (wall.size === '36x60') {
          qty36x60 += wall.quantity;
        } else if (wall.size === '48x72') {
          qty48x72 += wall.quantity;
        }
        
        // Count finishes
        finishes[wall.finish] = (finishes[wall.finish] || 0) + 1;
      });
      
      // Determine which finish to display
      const finishEntries = Object.entries(finishes);
      const mostCommonFinish = finishEntries.reduce((a, b) => a[1] > b[1] ? a : b)[0];
      
      // Walls count (install service only — walls don't matter for DIY
      // delivery, where the builder collapses to a single config)
      if (state.serviceType === 'delivery-installation') {
        const wallsRow = document.createElement('div');
        wallsRow.className = 'mirror-modal__summary-row';
        wallsRow.innerHTML = `
          <span class="mirror-modal__summary-label">Walls</span>
          <span class="mirror-modal__summary-value">${totalWalls}</span>
        `;
        wallsContainer.appendChild(wallsRow);
      }
      
      // 36"x60" mirror quantity (only if > 0)
      if (qty36x60 > 0) {
        const size36Row = document.createElement('div');
        size36Row.className = 'mirror-modal__summary-row';
        size36Row.innerHTML = `
          <span class="mirror-modal__summary-label">36"x60" mirror</span>
          <span class="mirror-modal__summary-value">${qty36x60}</span>
        `;
        wallsContainer.appendChild(size36Row);
      }
      
      // 48"x72" mirror quantity (only if > 0)
      if (qty48x72 > 0) {
        const size48Row = document.createElement('div');
        size48Row.className = 'mirror-modal__summary-row';
        size48Row.innerHTML = `
          <span class="mirror-modal__summary-label">48"x72" mirror</span>
          <span class="mirror-modal__summary-value">${qty48x72}</span>
        `;
        wallsContainer.appendChild(size48Row);
      }
      
      // Channel finish
      const finishRow = document.createElement('div');
      finishRow.className = 'mirror-modal__summary-row';
      finishRow.innerHTML = `
        <span class="mirror-modal__summary-label">Channel finish</span>
        <span class="mirror-modal__summary-value">${getFinishLabel(mostCommonFinish)}</span>
      `;
      wallsContainer.appendChild(finishRow);
    }

    // Update pricing breakdown
    updatePricingBreakdown();

    // Update add-ons summary
    const addonsContainer = document.querySelector('[data-summary-addons]');
    if (addonsContainer) {
      addonsContainer.innerHTML = '';
      let hasAddons = false;
      
      // Installation Finish (only after user selects). Shown for BOTH
      // services — for DIY it's the top hardware we ship (clips vs
      // J-channel), so the row reads "Mounting Hardware" there.
      if (state.addons.installationFinish) {
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">${state.serviceType === 'delivery-installation' ? 'Installation Finish' : 'Mounting Hardware'}</span>
          <span class="mirror-modal__summary-value">${state.addons.installationFinish === 'premium' ? 'Premium' : 'Standard'}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Adhesive (optional)
      if (state.addons.adhesive) {
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Adhesive</span>
          <span class="mirror-modal__summary-value">${state.walls.reduce((sum, wall) => sum + wall.quantity, 0)} ${state.walls.reduce((sum, wall) => sum + wall.quantity, 0) === 1 ? 'tube' : 'tubes'}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Cutouts (optional)
      if (state.addons.cutouts && state.addons.cutoutsCount > 0) {
        // Map outlet cover type value to readable label
        const outletCoverLabels = {
          'light-switch': 'Light Switch Cover',
          'standard-outlet': 'Standard Outlet Cover',
          'gfci': 'GFCI Cover',
          'other': 'Other',
          'not-sure': 'Not sure — see photo',
          'outlet': 'Outlet Cover',
          'both': 'Both (Light Switch + Outlet)'
        };
        const outletCoverLabel = outletCoverLabels[state.addons.outletCoverType] || state.addons.outletCoverType;
        
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Cutouts</span>
          <span class="mirror-modal__summary-value">${state.addons.cutoutsCount} × ${outletCoverLabel}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Wall Surface
      if (state.serviceType === 'delivery-installation' && state.addons.wallSurface) {
        const surfaceLabels = {
          'painted-drywall': 'Painted Drywall',
          'unpainted-drywall': 'Unpainted Drywall',
          'smooth-concrete': 'Smooth Concrete',
          'concrete-block': 'Concrete Block',
          'brick': 'Brick'
        };
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Wall Surface</span>
          <span class="mirror-modal__summary-value">${surfaceLabels[state.addons.wallSurface] || state.addons.wallSurface}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Show/hide add-ons section
      addonsContainer.style.display = hasAddons ? 'block' : 'none';
    }
  }

  function calculatePrice() {
    let total = 0;
    
    if (state.currentStep < 2) {
      state.totalPrice = 0;
      state.b2bMirrorTotals = null;
      updateEstimate();
      return;
    }

    // Get product data
    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    const addons = productData.addons || {};

    if (!window.mirrorConfigData) {
      return;
    }

    // Calculate mirror prices for all walls. For B2B buyers the trade catalog's
    // quantity price breaks apply per variant at checkout, so the estimate must
    // use break-aware pricing to match what they'll actually be charged.
    const b2bTotals = computeB2bMirrorTotals();
    state.b2bMirrorTotals = b2bTotals;
    if (b2bTotals) {
      total += b2bTotals.trade;
    } else {
      state.walls.forEach((wall) => {
        const mirrorPrice = getMirrorVariantPrice(mirrorProduct, wall.size, wall.finish);
        const wallTotal = mirrorPrice * wall.quantity;
        total += wallTotal;
      });
    }

    // Calculate total mirrors (for add-ons that charge per mirror)
    const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

    // Add-on: Installation ($50 per mirror)
    if (state.serviceType === 'delivery-installation' && addons.installation) {
      const installationPrice = getAddonPrice(addons.installation);
      const installationTotal = installationPrice * totalMirrors;
      total += installationTotal;
    }

    // Add-on: Premium Finish ($25 per mirror, only if installation selected and premium chosen)
    if (state.addons.installationFinish === 'premium' && addons.premiumFinish) {
      const premiumFinishPrice = getAddonPrice(addons.premiumFinish);
      const premiumFinishTotal = premiumFinishPrice * totalMirrors;
      total += premiumFinishTotal;
    }

    // Add-on: Adhesive ($15 per mirror — one tube each)
    if (state.addons.adhesive && addons.adhesive) {
      const adhesivePrice = getAddonPrice(addons.adhesive);
      total += adhesivePrice * totalMirrors;
    }

    // Add-on: Cutouts ($150 per cutout)
    if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
      const cutoutPrice = getAddonPrice(addons.cutout);
      const cutoutTotal = cutoutPrice * state.addons.cutoutsCount;
      total += cutoutTotal;
    }

    // Add-on: Wall Surface Surcharge ($50 per mirror, only for concrete/brick with installation)
    if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
      const surchargePrice = getAddonPrice(addons.wallSurcharge);
      const surchargeTotal = surchargePrice * totalMirrors;
      total += surchargeTotal;
    }

    state.totalPrice = total;
    updateEstimate();
  }

  function updatePricingBreakdown() {
    const pricingContainer = document.querySelector('[data-summary-pricing]');
    if (!pricingContainer) return;

    pricingContainer.innerHTML = '';

    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    const addons = productData.addons || {};

    let mirrorsTotal = 0;
    let installationTotal = 0;
    let premiumFinishTotal = 0;
    let adhesiveTotal = 0;
    let cutoutsTotal = 0;
    let wallSurchargeTotal = 0;
    const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

    // Calculate mirrors total (variant price already includes Matte Black surcharge).
    // B2B buyers get break-aware trade pricing — must match calculatePrice.
    const b2bTotals = computeB2bMirrorTotals();
    if (b2bTotals) {
      mirrorsTotal = b2bTotals.trade;
    } else {
      state.walls.forEach((wall) => {
        const mirrorPrice = getMirrorVariantPrice(mirrorProduct, wall.size, wall.finish);
        mirrorsTotal += mirrorPrice * wall.quantity;
      });
    }

    // Calculate installation if applicable
    if (state.serviceType === 'delivery-installation' && addons.installation) {
      const installationPrice = getAddonPrice(addons.installation);
      installationTotal = installationPrice * totalMirrors;
    }

    // Calculate premium finish if selected
    if (state.addons.installationFinish === 'premium' && addons.premiumFinish) {
      const premiumFinishPrice = getAddonPrice(addons.premiumFinish);
      premiumFinishTotal = premiumFinishPrice * totalMirrors;
    }

    // Calculate adhesive if selected (per mirror — one tube each)
    if (state.addons.adhesive && addons.adhesive) {
      const adhesivePrice = getAddonPrice(addons.adhesive);
      adhesiveTotal = adhesivePrice * totalMirrors;
    }

    // Calculate cutouts if selected
    if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
      const cutoutPrice = getAddonPrice(addons.cutout);
      cutoutsTotal = cutoutPrice * state.addons.cutoutsCount;
    }

    // Calculate wall surface surcharge if applicable
    if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
      const surchargePrice = getAddonPrice(addons.wallSurcharge);
      wallSurchargeTotal = surchargePrice * totalMirrors;
    }

    // Mirrors row
    const mirrorsRow = document.createElement('div');
    mirrorsRow.className = 'mirror-modal__summary-row';
    mirrorsRow.innerHTML = `
      <span class="mirror-modal__summary-label">Mirrors</span>
      <span class="mirror-modal__summary-value">${
        b2bTotals && b2bTotals.savings > 0.005
          ? `<s style="color:#8AA0BD;font-weight:400;margin-right:6px;">$${b2bTotals.list.toFixed(2)}</s>$${mirrorsTotal.toFixed(2)}`
          : `$${mirrorsTotal.toFixed(2)}`
      }</span>
    `;
    pricingContainer.appendChild(mirrorsRow);

    // Trade savings row (B2B only)
    if (b2bTotals && b2bTotals.savings > 0.005) {
      const tradeRow = document.createElement('div');
      tradeRow.className = 'mirror-modal__summary-row';
      tradeRow.innerHTML = `
        <span class="mirror-modal__summary-label" style="color:#2E9E7B;font-weight:600;">Trade discount applied</span>
        <span class="mirror-modal__summary-value" style="color:#2E9E7B;font-weight:600;">&minus;$${b2bTotals.savings.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(tradeRow);
    }

    // Installation row (only if installation selected)
    if (installationTotal > 0) {
      const installationRow = document.createElement('div');
      installationRow.className = 'mirror-modal__summary-row';
      installationRow.innerHTML = `
        <span class="mirror-modal__summary-label">Installation (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${installationTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(installationRow);
    }

    // Premium finish row (only if premium selected)
    if (premiumFinishTotal > 0) {
      const premiumRow = document.createElement('div');
      premiumRow.className = 'mirror-modal__summary-row';
      premiumRow.innerHTML = `
        <span class="mirror-modal__summary-label">Premium finish (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${premiumFinishTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(premiumRow);
    }

    // Adhesive row (only if adhesive selected)
    if (adhesiveTotal > 0) {
      const adhesiveRow = document.createElement('div');
      adhesiveRow.className = 'mirror-modal__summary-row';
      adhesiveRow.innerHTML = `
        <span class="mirror-modal__summary-label">Adhesive (${totalMirrors} ${totalMirrors === 1 ? 'mirror' : 'mirrors'})</span>
        <span class="mirror-modal__summary-value">$${adhesiveTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(adhesiveRow);
    }

    // Cutouts row (only if cutouts selected)
    if (cutoutsTotal > 0) {
      // Map outlet cover type value to readable label
      const outletCoverLabels = {
        'light-switch': 'Light Switch',
        'standard-outlet': 'Standard Outlet',
        'gfci': 'GFCI',
        'other': 'Other',
        'not-sure': 'Not sure — see photo',
        'outlet': 'Outlet',
        'both': 'Light Switch + Outlet'
      };
      const outletCoverLabel = outletCoverLabels[state.addons.outletCoverType] || state.addons.outletCoverType;
      
      const cutoutsRow = document.createElement('div');
      cutoutsRow.className = 'mirror-modal__summary-row';
      cutoutsRow.innerHTML = `
        <span class="mirror-modal__summary-label">Cutouts (${state.addons.cutoutsCount} × ${outletCoverLabel})</span>
        <span class="mirror-modal__summary-value">$${cutoutsTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(cutoutsRow);
    }

    // Wall surface row (only if surcharge applicable)
    if (wallSurchargeTotal > 0) {
      const wallSurfaceLabel = getWallSurfaceLabel(state.addons.wallSurface);
      const wallSurchargeRow = document.createElement('div');
      wallSurchargeRow.className = 'mirror-modal__summary-row';
      wallSurchargeRow.innerHTML = `
        <span class="mirror-modal__summary-label">${wallSurfaceLabel} (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${wallSurchargeTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(wallSurchargeRow);
    }
  }

  function getWallSurfaceLabel(wallSurface) {
    const labels = {
      'painted-drywall': 'Painted drywall',
      'unpainted-drywall': 'Unpainted drywall',
      'smooth-concrete': 'Smooth concrete',
      'concrete-block': 'Concrete block',
      'brick': 'Brick'
    };
    return labels[wallSurface] || wallSurface;
  }

  function parseShopifyVariantPrice(variant) {
    if (!variant || variant.price == null || variant.price === '') {
      return 0;
    }
    const raw = parseFloat(String(variant.price), 10);
    if (Number.isNaN(raw)) {
      return 0;
    }
    const str = String(variant.price);
    const hasDecimal = str.includes('.');
    if (!hasDecimal && raw >= 100) {
      return raw / 100;
    }
    return raw;
  }

  // --- B2B trade pricing -----------------------------------------------------
  // mirrorVariantCatalog entries carry compare_at_price and quantity_price_breaks
  // with real values only for logged-in company buyers (mirrorConfigData.b2b).

  function getB2bCatalogEntry(mirrorProduct, size, finish) {
    const data = window.mirrorConfigData || {};
    if (!data.b2b) return null;
    const variant = findMirrorVariant(mirrorProduct, size, finish);
    if (!variant) return null;
    const catalog = data.mirrorVariantCatalog || [];
    return catalog.find((v) => String(v.id) === String(variant.id)) || null;
  }

  // Unit pricing for a variant at a given total quantity: applies the best
  // quantity price break (same rule checkout uses) and exposes list price
  // (compare_at) plus the next break for the "add N more" nudge.
  function getB2bUnitPricing(mirrorProduct, size, finish, qty) {
    const entry = getB2bCatalogEntry(mirrorProduct, size, finish);
    if (!entry) return null;
    const base = parseShopifyVariantPrice(entry);
    if (base <= 0) return null;
    const list = entry.compare_at_price
      ? parseShopifyVariantPrice({ price: entry.compare_at_price })
      : 0;
    const breaks = (entry.quantity_price_breaks || [])
      .map((b) => ({ min: b.min, price: parseShopifyVariantPrice({ price: b.price }) }))
      .filter((b) => b.min > 0 && b.price > 0)
      .sort((a, b) => a.min - b.min);
    let unit = base;
    let nextBreak = null;
    breaks.forEach((b) => {
      if (qty >= b.min) unit = b.price;
    });
    for (let i = 0; i < breaks.length; i++) {
      if (qty < breaks[i].min) { nextBreak = breaks[i]; break; }
    }
    return { unit, base, list: list > unit ? list : Math.max(base, unit), nextBreak };
  }

  // Mirror totals across all walls at trade vs list pricing. Break qualification
  // is per variant across walls (two walls of the same size+finish combine in
  // the cart, so their quantities combine for the break too). Returns null for
  // non-B2B sessions so DTC behavior is untouched.
  function computeB2bMirrorTotals() {
    const data = window.mirrorConfigData || {};
    if (!data.b2b || !data.mirror) return null;
    const qtyByKey = {};
    state.walls.forEach((w) => {
      const k = w.size + '|' + w.finish;
      qtyByKey[k] = (qtyByKey[k] || 0) + w.quantity;
    });
    let trade = 0;
    let list = 0;
    let nudge = null;
    state.walls.forEach((w) => {
      const qty = qtyByKey[w.size + '|' + w.finish];
      const bp = getB2bUnitPricing(data.mirror, w.size, w.finish, qty);
      if (!bp) {
        const fallback = getMirrorVariantPrice(data.mirror, w.size, w.finish);
        trade += fallback * w.quantity;
        list += fallback * w.quantity;
        return;
      }
      trade += bp.unit * w.quantity;
      list += bp.list * w.quantity;
      if (bp.nextBreak) {
        const remaining = bp.nextBreak.min - qty;
        if (remaining > 0 && (!nudge || remaining < nudge.remaining)) {
          nudge = { remaining: remaining, price: bp.nextBreak.price };
        }
      }
    });
    return { trade: trade, list: list, savings: Math.max(0, list - trade), nudge: nudge };
  }

  // Renders the savings note under the running estimate (B2B only).
  // Full note goes below the total row as its own block; the mobile header
  // total gets a compact inline "Save $X" chip instead.
  function updateB2bEstimateNote(totalEl, compact) {
    if (!totalEl) return;
    const row = totalEl.closest('.mirror-modal__total-row');
    const anchor = (!compact && row) ? row : totalEl;
    let note = anchor.nextElementSibling;
    if (!note || !note.hasAttribute('data-b2b-note')) {
      note = document.createElement('span');
      note.setAttribute('data-b2b-note', '');
      note.style.cssText = compact
        ? 'display:inline-block;margin-left:8px;font-size:11px;font-weight:600;color:#2E9E7B;white-space:nowrap;'
        : 'display:block;margin:2px 0 6px;font-size:11.5px;font-weight:600;color:#2E9E7B;line-height:1.5;text-align:right;';
      anchor.insertAdjacentElement('afterend', note);
    }
    const totals = state.b2bMirrorTotals;
    if (!totals || totals.savings <= 0.005) {
      note.style.display = 'none';
      return;
    }
    if (compact) {
      note.style.display = 'inline-block';
      note.textContent = 'Save $' + totals.savings.toFixed(2);
      return;
    }
    const pct = totals.list > 0 ? Math.round((totals.savings / totals.list) * 100) : 0;
    let html = '<s style="color:#8AA0BD;font-weight:400;">$' + totals.list.toFixed(2) + ' list</s> ' +
      'Trade pricing — you save $' + totals.savings.toFixed(2) + (pct ? ' (' + pct + '%)' : '');
    if (totals.nudge && totals.nudge.remaining > 0) {
      html += '<span style="display:block;color:#405DE6;font-weight:500;">Add ' + totals.nudge.remaining +
        ' more mirror' + (totals.nudge.remaining > 1 ? 's' : '') + ' to unlock $' +
        totals.nudge.price.toFixed(2) + '/mirror</span>';
    }
    note.style.display = 'block';
    note.innerHTML = html;
  }

  function getDomMirrorFallbackPrice(size) {
    const el = document.querySelector(`[data-price-${size}]`);
    if (!el) {
      return null;
    }
    const num = parseFloat(String(el.textContent).replace(/[^0-9.]/g, ''), 10);
    if (Number.isNaN(num) || num <= 0) {
      return null;
    }
    return num;
  }

  function getMirrorVariantPrice(mirrorProduct, size, finish) {
    const variants = getMirrorVariantsSource(mirrorProduct);
    if (variants.length) {
      const variant = findMirrorVariant(mirrorProduct, size, finish);
      if (variant) {
        const p = parseShopifyVariantPrice(variant);
        if (p > 0) {
          return p;
        }
      }
    }

    const dom = getDomMirrorFallbackPrice(size);
    if (dom != null && dom > 0) {
      return dom;
    }

    return 0;
  }

  function getAddonPrice(addonProduct) {
    if (!addonProduct) {
      return 0;
    }
    
    // Try to get price from first variant (if add-on has variants)
    if (addonProduct.variants && addonProduct.variants.length > 0) {
      const price = parseFloat(addonProduct.variants[0].price) / 100;
      return price;
    }
    
    // Try to get direct price property
    if (addonProduct.price) {
      const price = parseFloat(addonProduct.price) / 100;
      return price;
    }
    
    return 0;
  }

  function needsWallSurcharge(wallSurface) {
    const surchargeTypes = ['smooth-concrete', 'concrete-block', 'brick'];
    return surchargeTypes.includes(wallSurface);
  }

  function getMirrorStartingPrice() {
    const data = window.mirrorConfigData || {};
    const variants = getMirrorVariantsSource(data.mirror);
    let min = 0;
    variants.forEach((v) => {
      const p = parseShopifyVariantPrice(v);
      if (p > 0 && (min === 0 || p < min)) min = p;
    });
    return min;
  }

  function updateEstimate() {
    const totalPriceEl = document.querySelector('[data-total-price]');
    const totalPriceMobileEl = document.querySelector('[data-total-price-mobile]');
    let priceFormatted = `$${state.totalPrice.toFixed(2)}`;
    // Step 1 zeroes the estimate by design (nothing chosen yet), but
    // "$0.00" read as broken/free and collected rage clicks in Clarity —
    // show the entry price instead until a mirror is actually priced.
    if (state.totalPrice === 0) {
      const from = getMirrorStartingPrice();
      priceFormatted = from > 0 ? `From $${from.toFixed(2)}` : '—';
    }
    
    if (totalPriceEl) {
      totalPriceEl.textContent = priceFormatted;
    }

    if (totalPriceMobileEl) {
      totalPriceMobileEl.textContent = priceFormatted;
    }

    updateB2bEstimateNote(totalPriceEl, false);
    updateB2bEstimateNote(totalPriceMobileEl, true);
  }

  /**
   * Each wall must use its own radio `name` group; duplicate names break multi-wall selection.
   */
  function applyWallRadioGroupNames(wallSection, wallIndex) {
    wallSection.querySelectorAll('.mirror-modal__size-radio').forEach((radio) => {
      radio.name = `mirror_size_wall_${wallIndex}`;
    });
    wallSection.querySelectorAll('.mirror-modal__finish-radio').forEach((radio) => {
      radio.name = `channel_finish_wall_${wallIndex}`;
    });
  }

  function initWallManagement() {
    const addWallBtn = document.querySelector('[data-action="add-wall"]');
    
    if (addWallBtn) {
      addWallBtn.addEventListener('click', addNewWall);
    }

    document.querySelectorAll('[data-wall-section]').forEach((section) => {
      const idx = parseInt(section.getAttribute('data-wall-section'), 10);
      if (!Number.isNaN(idx)) {
        applyWallRadioGroupNames(section, idx);
      }
    });

    // Initialize remove buttons for existing walls
    initRemoveButtons();
    updateRemoveButtonsVisibility();
  }

  function addNewWall() {
    const wallsContainer = document.querySelector('[data-walls-container]');
    const firstWall = document.querySelector('[data-wall-section="0"]');
    
    if (!wallsContainer || !firstWall) return;

    // Create new wall data
    const newWallIndex = state.walls.length;
    const newWall = {
      id: newWallIndex + 1,
      size: '36x60',
      finish: 'satin-silver',
      orientation: 'vertical',
      quantity: 1
    };
    state.walls.push(newWall);

    // Clone the first wall section
    const newWallSection = firstWall.cloneNode(true);
    newWallSection.setAttribute('data-wall-section', newWallIndex);
    
    // Update wall title
    const wallTitle = newWallSection.querySelector('.mirror-modal__wall-title');
    if (wallTitle) {
      wallTitle.textContent = `Wall ${newWallIndex + 1}`;
    }

    // Reset to default values
    resetWallSectionValues(newWallSection);
    applyWallRadioGroupNames(newWallSection, newWallIndex);

    // Insert before the "Add another wall" button
    const addWallField = document.querySelector('[data-action="add-wall"]').closest('.mirror-modal__field');
    wallsContainer.insertBefore(newWallSection, addWallField);

    // The clone inherited `data-md-bound="1"` from Wall 1's already-bound
    // controls, and the init*() guards below skip anything already flagged —
    // so without clearing it the new wall's size / finish / orientation / qty
    // controls never bind and stay dead (customers couldn't pick a mirror
    // size on Wall 2). Clear the flag on the clone so re-init binds it.
    newWallSection.querySelectorAll('[data-md-bound]').forEach((el) => {
      delete el.dataset.mdBound;
    });

    // Reinitialize event listeners for all walls
    initButtonOptions();
    initCardOptions();
    initQuantitySelectors();
    initRemoveButtons();
    updateRemoveButtonsVisibility();

    calculatePrice();
  }

  function resetWallSectionValues(wallSection) {
    // Reset size cards to default (36x60)
    wallSection.querySelectorAll('.mirror-modal__size-card').forEach((card, index) => {
      const radio = card.querySelector('.mirror-modal__size-radio');
      if (radio && radio.value === '36x60') {
        card.classList.add('mirror-modal__size-card--selected');
        radio.checked = true;
      } else {
        card.classList.remove('mirror-modal__size-card--selected');
        if (radio) radio.checked = false;
      }
    });

    // Reset finish cards to default (satin-silver)
    wallSection.querySelectorAll('.mirror-modal__finish-card').forEach((card, index) => {
      const radio = card.querySelector('.mirror-modal__finish-radio');
      if (radio && radio.value === 'satin-silver') {
        card.classList.add('mirror-modal__finish-card--selected');
        radio.checked = true;
      } else {
        card.classList.remove('mirror-modal__finish-card--selected');
        if (radio) radio.checked = false;
      }
    });

    // Reset orientation buttons to default (vertical as shown in Figma)
    wallSection.querySelectorAll('[data-field="orientation"]').forEach(btn => {
      if (btn.getAttribute('data-value') === 'vertical') {
        btn.classList.add('mirror-modal__orientation-btn--active');
      } else {
        btn.classList.remove('mirror-modal__orientation-btn--active');
      }
    });

    // Reset quantity to 1
    const qtyInput = wallSection.querySelector('[data-field="quantity"]');
    if (qtyInput) {
      qtyInput.value = 1;
    }
  }

  function initRemoveButtons() {
    const removeButtons = document.querySelectorAll('[data-remove-wall]');
    
    removeButtons.forEach(btn => {
      // Remove old listeners by cloning
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', () => {
        const wallIndex = parseInt(newBtn.getAttribute('data-remove-wall'));
        removeWall(wallIndex);
      });
    });
  }

  function removeWall(wallIndex) {
    if (state.walls.length === 1) {
      alert('You must have at least one wall configuration.');
      return;
    }

    // Remove from state
    state.walls.splice(wallIndex, 1);

    // Remove from DOM
    const wallSection = document.querySelector(`[data-wall-section="${wallIndex}"]`);
    if (wallSection) {
      wallSection.remove();
    }

    // Reindex all remaining wall sections
    const allWallSections = document.querySelectorAll('[data-wall-section]');
    allWallSections.forEach((section, index) => {
      section.setAttribute('data-wall-section', index);
      applyWallRadioGroupNames(section, index);

      const wallTitle = section.querySelector('.mirror-modal__wall-title');
      if (wallTitle) {
        wallTitle.textContent = `Wall ${index + 1}`;
      }

      const removeBtn = section.querySelector('[data-remove-wall]');
      if (removeBtn) {
        removeBtn.setAttribute('data-remove-wall', index);
      }
    });

    updateRemoveButtonsVisibility();
    calculatePrice();
  }

  function updateRemoveButtonsVisibility() {
    const removeButtons = document.querySelectorAll('[data-remove-wall]');
    const showRemove = state.walls.length > 1;
    
    removeButtons.forEach(btn => {
      btn.style.display = showRemove ? 'flex' : 'none';
    });
  }

  function getSizeLabel(size) {
    return size === '36x60' ? '36" × 60"' : '48" × 72"';
  }

  function getFinishLabel(finish) {
    return finish === 'satin-silver' ? 'Satin Silver' : 'Matte Black';
  }

  function getOrientationLabel(orientation) {
    return orientation === 'horizontal' ? 'Horizontal' : 'Vertical';
  }

  function initAddonProducts() {
    if (!window.mirrorConfigData) {
      return;
    }

    const addonProducts = window.mirrorConfigData.addonProducts || {};

    // Build addons object from product objects passed from Liquid
    const addons = {};

    if (addonProducts.installation && addonProducts.installation.variants && addonProducts.installation.variants.length > 0) {
      const variant = addonProducts.installation.variants[0];
      addons.installation = {
        title: addonProducts.installation.title || 'Professional Installation',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.premiumFinish && addonProducts.premiumFinish.variants && addonProducts.premiumFinish.variants.length > 0) {
      const variant = addonProducts.premiumFinish.variants[0];
      addons.premiumFinish = {
        title: addonProducts.premiumFinish.title || 'Premium Installation Finish',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.adhesive && addonProducts.adhesive.variants && addonProducts.adhesive.variants.length > 0) {
      const variant = addonProducts.adhesive.variants[0];
      addons.adhesive = {
        title: addonProducts.adhesive.title || 'Adhesive',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.cutout && addonProducts.cutout.variants && addonProducts.cutout.variants.length > 0) {
      const variant = addonProducts.cutout.variants[0];
      addons.cutout = {
        title: addonProducts.cutout.title || 'Outlet/Switch Cutout + Cover',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.wallSurcharge && addonProducts.wallSurcharge.variants && addonProducts.wallSurcharge.variants.length > 0) {
      const variant = addonProducts.wallSurcharge.variants[0];
      addons.wallSurcharge = {
        title: addonProducts.wallSurcharge.title || 'Wall Surface Surcharge',
        price: variant.price,
        variants: [variant]
      };
    }

    window.mirrorConfigData.addons = addons;
  }

  function syncSizeCardPrices() {
    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    if (!mirrorProduct) return;

    ['36x60', '48x72'].forEach(size => {
      const variant = findMirrorVariant(mirrorProduct, size, 'satin-silver');
      if (!variant) return;
      const price = parseShopifyVariantPrice(variant);
      if (price <= 0) return;
      document.querySelectorAll(`[data-price-${size}]`).forEach(el => {
        el.textContent = `$${price.toFixed(2)}`;
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Initialize addon products from hardcoded variant IDs
    initAddonProducts();
    
    // Initialize everything
    initModal();
    initAccordion();
    initOptions();
    initButtonOptions();
    initCardOptions();
    initQuantitySelectors();
    initCheckboxes();
    initSelects();
    initPhotoUpload();
    initNavigation();
    initWallManagement();
    syncSizeCardPrices();
    updateSummary();
    calculatePrice();
    updateConditionalFields();
    updateStep();
  });
})();
